import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash, FloppyDisk, Lightning, CalendarBlank, ClockCounterClockwise, Minus, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin tab for entering legacy salary + leave-balance history.
 * Three modes:
 *   - Yearly:   one row per calendar year (legacy default)
 *   - Quick:    just total days + avg salary
 *   - Period:   arbitrary date-ranges with their own salary + days
 * Includes an audit-log viewer for compliance.
 */
export default function YearlyHistoryTab({ employeeId, language }) {
  const isAr = language === 'ar';
  const T = (a, e) => (isAr ? a : e);
  const [rows, setRows] = useState([]);
  const [breakdown, setBreakdown] = useState(null);
  const [hireYear, setHireYear] = useState(null);
  const [hireDate, setHireDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState('period'); // 'yearly' | 'quick' | 'period' | 'debit'
  const [quickAvgSalary, setQuickAvgSalary] = useState('');
  const [quickTotalDays, setQuickTotalDays] = useState('');
  // Period mode
  const [periods, setPeriods] = useState([]);
  // Opening-debit mode (negative carryover from legacy systems)
  const [debitDays, setDebitDays] = useState('');
  const [debitSalary, setDebitSalary] = useState('');
  const [debitDate, setDebitDate] = useState('');
  const [debitNote, setDebitNote] = useState('');
  // Audit log
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditRows, setAuditRows] = useState([]);

  const load = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const [{ data }, empRes] = await Promise.all([
        axios.get(`${API}/accruals/balance/${employeeId}`, { withCredentials: true }),
        axios.get(`${API}/employees/${employeeId}`, { withCredentials: true }).catch(() => null),
      ]);
      setBreakdown(data);
      const hire = empRes?.data?.hire_date || empRes?.data?.contract_start_date;
      if (hire) {
        setHireYear(parseInt(hire.slice(0, 4), 10));
        setHireDate(hire.slice(0, 10));
      }
      // Hydrate yearly rows from legacy slices (one row per year)
      const yearly = (data.slices || [])
        .filter(s => s.is_legacy_yearly || /-12$/.test(s.month))
        .map(s => ({
          year: parseInt(s.month.split('-')[0], 10),
          monthly_salary: s.monthly_salary,
          days_accrued: s.days_accrued,
          days_consumed: Math.max(0, (s.days_accrued || 0) - (s.days_remaining || 0)),
        }));
      if (yearly.length > 0) setRows(yearly);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  const loadAudit = useCallback(async () => {
    if (!employeeId) return;
    try {
      const { data } = await axios.get(
        `${API}/accruals/legacy/${employeeId}/audit`, { withCredentials: true }
      );
      setAuditRows(data || []);
    } catch (e) {
      console.error(e);
    }
  }, [employeeId]);

  useEffect(() => { load(); }, [load]);

  const addRow = () => {
    setRows(prev => {
      const lastYear = prev.length ? Math.max(...prev.map(r => Number(r.year || 0))) : new Date().getFullYear() - 1;
      return [...prev, { year: lastYear + 1, monthly_salary: '', days_accrued: 21, days_consumed: 0 }];
    });
  };
  const removeRow = (idx) => setRows(prev => prev.filter((_, i) => i !== idx));
  const updateRow = (idx, field, value) =>
    setRows(prev => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));

  // ===== Period buckets =====
  const addPeriod = () => {
    const last = periods[periods.length - 1];
    const nextFrom = last?.to_date
      ? new Date(new Date(last.to_date).getTime() + 86400000).toISOString().slice(0, 10)
      : (hireDate || new Date().toISOString().slice(0, 10));
    setPeriods([...periods, {
      from_date: nextFrom,
      to_date: new Date().toISOString().slice(0, 10),
      monthly_salary: '',
      days_remaining: '',
      note: '',
    }]);
  };
  const removePeriod = (idx) => setPeriods(periods.filter((_, i) => i !== idx));
  const updatePeriod = (idx, field, value) =>
    setPeriods(periods.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));

  const periodTotal = periods.reduce(
    (acc, p) => acc + (parseFloat(p.days_remaining) || 0), 0
  );
  const periodValue = periods.reduce(
    (acc, p) => acc + ((parseFloat(p.monthly_salary) || 0) / 30) * (parseFloat(p.days_remaining) || 0), 0
  );

  const total = rows.reduce((acc, r) => {
    const accrued = parseFloat(r.days_accrued || 0);
    const consumed = parseFloat(r.days_consumed || 0);
    return acc + Math.max(0, accrued - consumed);
  }, 0);

  const submitPeriods = async () => {
    if (periods.length === 0) {
      toast.error(T('أدخل شريحة واحدة على الأقل', 'Add at least one bucket'));
      return;
    }
    for (const p of periods) {
      if (!p.from_date || !p.to_date || !p.monthly_salary || p.days_remaining === '' || p.days_remaining == null) {
        toast.error(T('يرجى إكمال جميع الحقول', 'Please complete all fields'));
        return;
      }
      if (p.from_date > p.to_date) {
        toast.error(T(`الفترة ${p.from_date} → ${p.to_date} غير صحيحة`, `Period ${p.from_date} → ${p.to_date} invalid`));
        return;
      }
      if (hireDate && p.from_date < hireDate) {
        toast.error(T(
          `الفترة تبدأ ${p.from_date} قبل تاريخ التعيين (${hireDate})`,
          `Period starts ${p.from_date} before hire date (${hireDate})`,
        ));
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        buckets: periods.map(p => ({
          from_date: p.from_date,
          to_date: p.to_date,
          monthly_salary: parseFloat(p.monthly_salary),
          days_remaining: parseFloat(p.days_remaining),
          note: p.note || '',
        })),
      };
      const { data } = await axios.put(
        `${API}/accruals/legacy/${employeeId}/periods`,
        payload,
        { withCredentials: true },
      );
      toast.success(T(
        `تم. ${data.buckets_created} شريحة | ${data.total_days_remaining} يوم بقيمة ${data.total_value_sar} ر.س`,
        `Saved. ${data.buckets_created} buckets | ${data.total_days_remaining} days = ${data.total_value_sar} SAR`,
      ));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const submitYearly = async () => {
    if (rows.length === 0) {
      toast.error(T('أدخل صفًّا واحدًا على الأقل', 'Add at least one row'));
      return;
    }
    for (const r of rows) {
      if (!r.year || !r.monthly_salary || r.days_accrued == null) {
        toast.error(T('يرجى إكمال جميع الحقول', 'Please complete all fields'));
        return;
      }
      if (hireYear && parseInt(r.year, 10) < hireYear) {
        toast.error(T(
          `عام ${r.year} قبل تاريخ التعيين (${hireYear}). لا يمكن تسجيل رصيد لسنوات قبل التوظيف.`,
          `Year ${r.year} is before hire year (${hireYear}). Cannot record balance for years before employment.`,
        ));
        return;
      }
      if (parseFloat(r.days_consumed || 0) > parseFloat(r.days_accrued || 0) + 0.01) {
        toast.error(T(`عام ${r.year}: المستهلك أكبر من المكتسب`, `Year ${r.year}: consumed > accrued`));
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        entries: rows.map(r => ({
          year: parseInt(r.year, 10),
          monthly_salary: parseFloat(r.monthly_salary),
          days_accrued: parseFloat(r.days_accrued),
          days_consumed: parseFloat(r.days_consumed || 0),
        })),
      };
      const { data } = await axios.put(
        `${API}/accruals/legacy/${employeeId}/yearly`,
        payload,
        { withCredentials: true },
      );
      toast.success(T(
        `تم. تم إنشاء ${data.slices_created} شريحة | الرصيد ${data.total_days_remaining} يوم بقيمة ${data.total_value_sar} ر.س`,
        `Saved. ${data.slices_created} slices | ${data.total_days_remaining} days = ${data.total_value_sar} SAR`,
      ));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const submitQuick = async () => {
    const avg = parseFloat(quickAvgSalary);
    const days = parseFloat(quickTotalDays);
    if (!(avg > 0) || days < 0) {
      toast.error(T('الرجاء إدخال أرقام صالحة', 'Please enter valid numbers'));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.put(
        `${API}/accruals/legacy/${employeeId}/quick`,
        { avg_monthly_salary: avg, total_days_remaining: days, note: '' },
        { withCredentials: true },
      );
      toast.success(T(
        `تم. الرصيد ${data.total_days_remaining} يوم بقيمة ${data.total_value_sar} ر.س`,
        `Saved. ${data.total_days_remaining} days = ${data.total_value_sar} SAR`,
      ));
      setQuickAvgSalary(''); setQuickTotalDays('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const clearLegacy = async () => {
    if (!window.confirm(T('سيتم حذف كل سجل الرصيد لهذا الموظف وإعادة بناء snapshot جديد بالراتب الحالي. متابعة؟',
                          'This wipes all accrual slices and rebuilds a snapshot with current salary. Continue?'))) return;
    setSubmitting(true);
    try {
      await axios.delete(`${API}/accruals/legacy/${employeeId}`, { withCredentials: true });
      toast.success(T('تم المسح', 'Cleared'));
      setRows([]);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const submitOpeningDebit = async () => {
    const days = parseFloat(debitDays);
    const salary = parseFloat(debitSalary);
    if (!(days > 0)) { toast.error(T('عدد الأيام المستحقة يجب أن يكون أكبر من صفر', 'Days owed must be > 0')); return; }
    if (!(salary > 0)) { toast.error(T('راتب التاريخ المؤثر يجب أن يكون أكبر من صفر', 'Salary must be > 0')); return; }
    if (!debitDate) { toast.error(T('اختر التاريخ المؤثر', 'Pick the effective date')); return; }
    if (!window.confirm(T(
      `سيتم إضافة رصيد افتتاحي بـ -${days} يوم. يبقى رصيد الموظف سالباً حتى يقاصّه التراكم الشهري. متابعة؟`,
      `An opening-debit slice of -${days} days will be added. Balance stays negative until offset by monthly accruals. Continue?`
    ))) return;
    setSubmitting(true);
    try {
      const { data } = await axios.put(
        `${API}/accruals/legacy/${employeeId}/opening-debit`,
        { days_owed: days, monthly_salary: salary, effective_date: debitDate, note: debitNote || '' },
        { withCredentials: true },
      );
      toast.success(T(
        `تم. الرصيد الصافي الحالي: ${data.current_net_balance} يوم`,
        `Saved. Net balance: ${data.current_net_balance} d`,
      ));
      setDebitDays(''); setDebitSalary(''); setDebitDate(''); setDebitNote('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const clearOpeningDebit = async () => {
    if (!window.confirm(T('سيتم حذف الرصيد الافتتاحي السالب فقط. متابعة؟', 'This removes only the opening-debit slice. Continue?'))) return;
    setSubmitting(true);
    try {
      await axios.delete(`${API}/accruals/legacy/${employeeId}/opening-debit`, { withCredentials: true });
      toast.success(T('تم الحذف', 'Removed'));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  // Detect an existing opening-debit slice so the form can show its current value.
  const existingDebit = (breakdown?.slices || []).find(s => s.is_opening_debit);

  return (
    <TabsContent value="history" className="space-y-3">
      {/* Summary card */}
      <div className="rounded-md border border-[#C4BFF0] bg-[#F8F7FC] p-3 grid grid-cols-3 gap-2 text-xs">
        <div>
          <div className="text-[#655dc6]/70">{T('إجمالي الرصيد', 'Total balance')}</div>
          <div
            className={`font-bold text-base ${(breakdown?.total_days_remaining ?? 0) < 0 ? 'text-red-600' : 'text-[#655dc6]'}`}
            data-testid="legacy-total-days"
          >
            {breakdown?.total_days_remaining ?? 0} {T('يوم', 'd')}
            {(breakdown?.total_days_remaining ?? 0) < 0 && (
              <span className="ms-1 text-[10px] font-normal text-red-500">
                {T('(دَين)', '(debt)')}
              </span>
            )}
          </div>
        </div>
        <div>
          <div className="text-[#655dc6]/70">{T('القيمة النقدية', 'Cash value')}</div>
          <div className="font-bold text-base text-[#10B981]" data-testid="legacy-total-value">
            {(breakdown?.total_value_sar ?? 0).toLocaleString()} {T('ر.س', 'SAR')}
          </div>
        </div>
        <div>
          <div className="text-[#655dc6]/70">{T('متوسط اليوم', 'Avg / day')}</div>
          <div className="font-bold text-base text-[#655dc6]/80">
            {(breakdown?.weighted_avg_daily_rate ?? 0).toLocaleString()} {T('ر.س', 'SAR')}
          </div>
        </div>
      </div>

      {/* Mode switcher */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Button
          size="sm" variant={mode === 'period' ? 'default' : 'outline'}
          onClick={() => setMode('period')}
          className={mode === 'period' ? 'bg-[#655dc6] hover:bg-[#544cb0]' : 'border-[#655dc6] text-[#655dc6]'}
          data-testid="mode-period-btn"
        ><CalendarBlank size={14} className="me-1" />{T('فترات مخصصة (موصى به)', 'Period buckets (recommended)')}</Button>
        <Button
          size="sm" variant={mode === 'yearly' ? 'default' : 'outline'}
          onClick={() => setMode('yearly')}
          className={mode === 'yearly' ? 'bg-[#655dc6] hover:bg-[#544cb0]' : 'border-[#655dc6] text-[#655dc6]'}
          data-testid="mode-yearly-btn"
        >{T('سنة بسنة', 'Year-by-year')}</Button>
        <Button
          size="sm" variant={mode === 'quick' ? 'default' : 'outline'}
          onClick={() => setMode('quick')}
          className={mode === 'quick' ? 'bg-[#655dc6] hover:bg-[#544cb0]' : 'border-[#655dc6] text-[#655dc6]'}
          data-testid="mode-quick-btn"
        ><Lightning size={14} className="me-1" />{T('إدخال سريع', 'Quick entry')}</Button>
        <Button
          size="sm" variant={mode === 'debit' ? 'default' : 'outline'}
          onClick={() => setMode('debit')}
          className={mode === 'debit' ? 'bg-red-600 hover:bg-red-700 text-white' : 'border-red-300 text-red-600 hover:bg-red-50'}
          data-testid="mode-debit-btn"
        ><Minus size={14} className="me-1" />{T('رصيد افتتاحي سالب', 'Opening debit')}</Button>
        <div className="flex-1" />
        <Button size="sm" variant="ghost"
                onClick={() => { setAuditOpen(o => !o); if (!auditOpen) loadAudit(); }}
                className="text-[#655dc6] hover:bg-[#EDE9FE]" data-testid="toggle-audit-btn">
          <ClockCounterClockwise size={14} className="me-1" />{T('السجل', 'Audit log')}
        </Button>
        <Button size="sm" variant="ghost" onClick={clearLegacy} disabled={submitting}
                className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="clear-legacy-btn">
          <Trash size={14} className="me-1" />{T('مسح', 'Clear')}
        </Button>
      </div>

      {/* Audit log viewer */}
      {auditOpen && (
        <div className="rounded-md border border-[#C4BFF0] bg-white p-3 max-h-56 overflow-y-auto" data-testid="audit-panel">
          <div className="text-xs font-semibold text-[#655dc6] mb-2">
            {T('سجل تعديلات الرصيد اليدوي', 'Manual balance audit log')}
          </div>
          {auditRows.length === 0 ? (
            <div className="text-xs text-[#655dc6]/60 text-center py-3">
              {T('لا توجد عمليات مسجلة بعد', 'No actions recorded yet')}
            </div>
          ) : (
            <ul className="space-y-1.5 text-xs">
              {auditRows.map((a, i) => (
                <li key={i} className="border-b border-[#EDE9FE] pb-1.5 last:border-b-0">
                  <div className="flex justify-between gap-2">
                    <span className="font-semibold text-[#655dc6]">{a.action}</span>
                    <span className="text-[#655dc6]/60">{(a.performed_at || '').slice(0, 19).replace('T', ' ')}</span>
                  </div>
                  <div className="text-[#655dc6]/70">
                    {T('بواسطة', 'By')}: <strong>{a.performed_by_name || a.performed_by}</strong>
                    {a.summary && (
                      <> · {a.summary.buckets_created || a.summary.slices_created || 0} {T('شريحة', 'slices')}, {a.summary.total_days_remaining} {T('يوم', 'd')} = {a.summary.total_value_sar} {T('ر.س', 'SAR')}</>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {mode === 'period' ? (
        // ---------- Period buckets mode (NEW — recommended for legacy hires) ----------
        <div className="space-y-2">
          <p className="text-xs text-[#655dc6]/70 leading-relaxed">
            {T(
              'هذا هو الخيار الموصى به للموظفين القدامى: حدّد فترات الرواتب المختلفة بالضبط مع عدد الأيام المتبقي من كل فترة. يتم استهلاك الأيام من الفترة الأقدم أولاً.',
              'Recommended for legacy hires: enter the exact periods with different salaries and the days remaining from each. Days are consumed oldest-first.',
            )}
          </p>
          <div className="overflow-x-auto rounded border border-[#C4BFF0]">
            <table className="w-full text-xs">
              <thead className="bg-[#E8E6F5] text-[#655dc6]">
                <tr>
                  <th className="px-2 py-2 text-start">{T('من تاريخ', 'From')}</th>
                  <th className="px-2 py-2 text-start">{T('إلى تاريخ', 'To')}</th>
                  <th className="px-2 py-2 text-start">{T('الراتب الشهري', 'Monthly Salary')}</th>
                  <th className="px-2 py-2 text-start">{T('أيام متبقية', 'Days Remaining')}</th>
                  <th className="px-2 py-2 text-start">{T('قيمة اليوم', 'Daily Rate')}</th>
                  <th className="px-2 py-2 text-start">{T('ملاحظة', 'Note')}</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {periods.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-[#655dc6]/60 py-4">
                    {T('لم يتم إضافة أي شريحة بعد', 'No buckets yet')}
                  </td></tr>
                )}
                {periods.map((p, i) => {
                  const dailyRate = (parseFloat(p.monthly_salary) || 0) / 30;
                  return (
                    <tr key={i} className="border-t border-[#C4BFF0]/50">
                      <td className="p-1">
                        <Input type="date" value={p.from_date}
                               onChange={(e) => updatePeriod(i, 'from_date', e.target.value)}
                               min={hireDate || undefined}
                               className="h-7 text-xs w-36" data-testid={`period-from-${i}`} />
                      </td>
                      <td className="p-1">
                        <Input type="date" value={p.to_date}
                               onChange={(e) => updatePeriod(i, 'to_date', e.target.value)}
                               className="h-7 text-xs w-36" data-testid={`period-to-${i}`} />
                      </td>
                      <td className="p-1">
                        <Input type="number" step="0.01" value={p.monthly_salary}
                               onChange={(e) => updatePeriod(i, 'monthly_salary', e.target.value)}
                               placeholder="7500"
                               className="h-7 text-xs w-28" data-testid={`period-salary-${i}`} />
                      </td>
                      <td className="p-1">
                        <Input type="number" step="0.5" value={p.days_remaining}
                               onChange={(e) => updatePeriod(i, 'days_remaining', e.target.value)}
                               placeholder="14.5"
                               className="h-7 text-xs w-24" data-testid={`period-days-${i}`} />
                      </td>
                      <td className="p-1 text-[#655dc6]/80">{dailyRate.toFixed(2)}</td>
                      <td className="p-1">
                        <Input value={p.note}
                               onChange={(e) => updatePeriod(i, 'note', e.target.value)}
                               placeholder={T('اختياري', 'optional')}
                               className="h-7 text-xs w-40" data-testid={`period-note-${i}`} />
                      </td>
                      <td className="p-1">
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500"
                                onClick={() => removePeriod(i)} data-testid={`period-remove-${i}`}>
                          <Trash size={12} />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {periods.length > 0 && (
                  <tr className="border-t-2 border-[#655dc6] bg-[#F8F7FC] font-bold">
                    <td colSpan={3} className="p-2 text-[#655dc6]">{T('الإجمالي', 'Total')}</td>
                    <td className="p-2 text-[#10B981]">{periodTotal.toFixed(1)} {T('يوم', 'd')}</td>
                    <td colSpan={2} className="p-2 text-[#10B981]">
                      ≈ {periodValue.toLocaleString(undefined, { maximumFractionDigits: 2 })} {T('ر.س', 'SAR')}
                    </td>
                    <td></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={addPeriod}
                    className="gap-1 border-[#655dc6] text-[#655dc6]" data-testid="add-period-btn">
              <Plus size={14} />{T('إضافة شريحة فترة', 'Add bucket')}
            </Button>
            <div className="flex-1" />
            <Button size="sm" onClick={submitPeriods} disabled={submitting || loading}
                    className="bg-[#655dc6] hover:bg-[#544cb0] gap-1" data-testid="save-periods-btn">
              <FloppyDisk size={14} />
              {submitting ? '...' : T('حفظ الفترات', 'Save buckets')}
            </Button>
          </div>
        </div>
      ) : mode === 'yearly' ? (
        // ---------- Yearly mode (legacy) ----------
        <div className="space-y-2">
          <div className="overflow-x-auto rounded border border-[#C4BFF0]">
            <table className="w-full text-xs">
              <thead className="bg-[#E8E6F5] text-[#655dc6]">
                <tr>
                  <th className="px-2 py-2 text-start">{T('السنة', 'Year')}</th>
                  <th className="px-2 py-2 text-start">{T('الراتب الشهري', 'Monthly Salary')}</th>
                  <th className="px-2 py-2 text-start">{T('أيام مكتسبة', 'Days Accrued')}</th>
                  <th className="px-2 py-2 text-start">{T('أيام مستهلكة', 'Days Consumed')}</th>
                  <th className="px-2 py-2 text-start">{T('المتبقي', 'Remaining')}</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-[#655dc6]/60 py-4">
                    {T('لم يتم إدخال أي سجلات بعد', 'No entries yet')}
                  </td></tr>
                )}
                {rows.map((r, i) => {
                  const remaining = Math.max(0, parseFloat(r.days_accrued || 0) - parseFloat(r.days_consumed || 0));
                  return (
                    <tr key={i} className="border-t border-[#C4BFF0]/50">
                      <td className="p-1">
                        <Input type="number" value={r.year} onChange={(e) => updateRow(i, 'year', e.target.value)}
                               className="h-7 text-xs w-20" data-testid={`row-year-${i}`} />
                      </td>
                      <td className="p-1">
                        <Input type="number" step="0.01" value={r.monthly_salary}
                               onChange={(e) => updateRow(i, 'monthly_salary', e.target.value)}
                               className="h-7 text-xs w-32" data-testid={`row-salary-${i}`} />
                      </td>
                      <td className="p-1">
                        <Input type="number" step="0.5" value={r.days_accrued}
                               onChange={(e) => updateRow(i, 'days_accrued', e.target.value)}
                               className="h-7 text-xs w-24" data-testid={`row-accrued-${i}`} />
                      </td>
                      <td className="p-1">
                        <Input type="number" step="0.5" value={r.days_consumed}
                               onChange={(e) => updateRow(i, 'days_consumed', e.target.value)}
                               className="h-7 text-xs w-24" data-testid={`row-consumed-${i}`} />
                      </td>
                      <td className="p-1 font-semibold text-[#10B981]">{remaining.toFixed(1)}</td>
                      <td className="p-1">
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500" onClick={() => removeRow(i)}>
                          <Trash size={12} />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {rows.length > 0 && (
                  <tr className="border-t-2 border-[#655dc6] bg-[#F8F7FC] font-bold">
                    <td colSpan={4} className="p-2 text-[#655dc6]">{T('الإجمالي المتبقي', 'Total remaining')}</td>
                    <td className="p-2 text-[#10B981]">{total.toFixed(1)} {T('يوم', 'd')}</td>
                    <td></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={addRow} className="gap-1 border-[#655dc6] text-[#655dc6]">
              <Plus size={14} />{T('إضافة سنة', 'Add year')}
            </Button>
            <div className="flex-1" />
            <Button size="sm" onClick={submitYearly} disabled={submitting || loading}
                    className="bg-[#655dc6] hover:bg-[#544cb0] gap-1" data-testid="save-yearly-btn">
              <FloppyDisk size={14} />
              {submitting ? '...' : T('حفظ', 'Save')}
            </Button>
          </div>
        </div>
      ) : (
        // ---------- Quick mode ----------
        <div className="space-y-3">
          <p className="text-xs text-[#655dc6]/70 leading-relaxed">
            {T(
              'استخدم الإدخال السريع إذا كنت تعرف الإجمالي ومتوسط الراتب فقط. سيُنشئ النظام شريحة snapshot واحدة بهذه القيم.',
              'Use Quick Entry if you only know the total balance and average historic salary. Creates one snapshot slice.',
            )}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{T('متوسط الراتب الشهري', 'Avg Monthly Salary')}</Label>
              <Input type="number" step="0.01" value={quickAvgSalary}
                     onChange={(e) => setQuickAvgSalary(e.target.value)}
                     placeholder="6500" className="h-8 text-xs" data-testid="quick-salary-input" />
            </div>
            <div>
              <Label className="text-xs">{T('إجمالي الأيام المتبقية', 'Total Days Remaining')}</Label>
              <Input type="number" step="0.5" value={quickTotalDays}
                     onChange={(e) => setQuickTotalDays(e.target.value)}
                     placeholder="48" className="h-8 text-xs" data-testid="quick-days-input" />
            </div>
          </div>
          <div className="flex justify-end">
            <Button size="sm" onClick={submitQuick} disabled={submitting}
                    className="bg-[#655dc6] hover:bg-[#544cb0] gap-1" data-testid="save-quick-btn">
              <FloppyDisk size={14} />
              {submitting ? '...' : T('حفظ', 'Save')}
            </Button>
          </div>
        </div>
      )}

      {/* Opening-debit form — single negative slice that monthly accrual offsets. */}
      {mode === 'debit' && (
        <div className="rounded-lg border-2 border-red-200 bg-red-50/40 p-4 space-y-4" data-testid="opening-debit-form">
          <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-3 text-[11px] text-amber-900">
            <Warning size={18} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
            <div className="leading-relaxed">
              <p className="font-semibold mb-1">{T('متى تستخدم هذا الوضع؟', 'When to use this mode?')}</p>
              <p>
                {T(
                  'يُستخدم لتسجيل أيام مستحقة على الموظف من نظام سابق (أخذ إجازات قبل استحقاقها). يتم إنشاء شريحة افتتاحية واحدة بقيمة سالبة، ويبقى الرصيد الصافي سالباً حتى يقاصّه التراكم الشهري التلقائي. عند الوصول إلى الصفر يبدأ الموظف بأخذ إجازات بشكل طبيعي.',
                  'For migrating an employee with "days owed" from a previous system (took leave before earning it). A single negative slice is created and the net balance stays negative until monthly accruals offset it. Once it reaches zero, the employee can take leave normally.'
                )}
              </p>
            </div>
          </div>

          {existingDebit && (
            <div className="flex items-center justify-between gap-3 rounded-md bg-white border border-red-200 p-3" data-testid="existing-debit-info">
              <div className="text-xs text-[#1F2937]">
                <span className="font-semibold text-red-700">
                  {T('رصيد افتتاحي حالي:', 'Current opening debit:')} {existingDebit.days_remaining}
                </span>
                <span className="text-[#655dc6]/70 ms-2">
                  · {T('الشهر', 'Month')}: <strong>{existingDebit.month}</strong>
                </span>
                <span className="text-[#655dc6]/70 ms-2">
                  · {T('الراتب وقتها', 'Salary at the time')}: <strong>{existingDebit.monthly_salary}</strong>
                </span>
              </div>
              <Button size="sm" variant="outline" onClick={clearOpeningDebit} disabled={submitting}
                      className="border-red-300 text-red-700 hover:bg-red-50" data-testid="clear-debit-btn">
                <Trash size={14} className="me-1" />{T('حذف', 'Remove')}
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">{T('عدد الأيام المستحقة على الموظف', 'Days owed by employee')}</Label>
              <Input
                type="number" min="0.5" step="0.5" max="365" dir="ltr"
                value={debitDays}
                onChange={(e) => setDebitDays(e.target.value)}
                placeholder="10"
                className="border-red-200 focus:ring-red-500"
                data-testid="debit-days-input"
              />
              <p className="text-[10px] text-red-600/80">
                {T('سيُسجّل كرصيد سالب', 'Stored as a negative balance')}
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{T('الراتب الشهري وقتها', 'Monthly salary at that time')}</Label>
              <Input
                type="number" min="1" dir="ltr"
                value={debitSalary}
                onChange={(e) => setDebitSalary(e.target.value)}
                placeholder="5000"
                className="border-red-200 focus:ring-red-500"
                data-testid="debit-salary-input"
              />
              <p className="text-[10px] text-[#655dc6]/70">
                {T('لحساب القيمة المالية المرجعية', 'For reference cash value calc')}
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{T('التاريخ المؤثر', 'Effective date')}</Label>
              <Input
                type="date" dir="ltr"
                value={debitDate || hireDate || ''}
                onChange={(e) => setDebitDate(e.target.value)}
                className="border-red-200 focus:ring-red-500"
                data-testid="debit-date-input"
              />
              <p className="text-[10px] text-[#655dc6]/70">
                {T('عادةً تاريخ التعيين أو الترحيل', 'Usually hire/migration date')}
              </p>
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">{T('ملاحظة (اختياري)', 'Note (optional)')}</Label>
            <Input
              type="text"
              value={debitNote}
              onChange={(e) => setDebitNote(e.target.value)}
              placeholder={T('مثال: أيام مستحقة من النظام السابق قبل تاريخ الترحيل', 'e.g. Days owed from legacy system pre-migration')}
              className="border-red-200 focus:ring-red-500"
              data-testid="debit-note-input"
            />
          </div>

          <div className="flex justify-end">
            <Button
              size="sm" onClick={submitOpeningDebit} disabled={submitting}
              className="bg-red-600 hover:bg-red-700 text-white gap-1"
              data-testid="save-debit-btn"
            >
              <FloppyDisk size={14} />
              {submitting ? '...' : T('حفظ الرصيد السالب', 'Save opening debit')}
            </Button>
          </div>
        </div>
      )}
    </TabsContent>
  );
}
